import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Alert,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Collapse,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Assessment as AssessmentIcon,
  EmojiEvents as RankingIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Circle as CircleIcon,
  ExitToApp as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  SportsHandball as BadmintonIcon,
  SportsCricket as CricketIcon,
  SportsTennis as TennisIcon,
  SportsHockey as IceHockeyIcon,
  SportsSoccer as FootballIcon,
  Sports as AthleticsIcon,
  Star as StarIcon,
  EmojiEvents as MedalIcon,
  Diamond as DiamondIcon,
  Edit as EditIcon,
  Note as NoteIcon,
  Assignment as AssignmentIcon,
  SportsTennis as ShuttleIcon,
  List as ListIcon,
} from '@mui/icons-material';
import { useAuthStore } from '@store/authStore';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import EncycdataLogo from '@components/layout/EncycdataLogo';
import RankingEvaluationWithTabs, {
  type RankingEvaluationEmbeddedState,
  type RankingEvaluationWithTabsHandle,
} from '@components/performance/RankingEvaluationWithTabs';
import TournamentRanking from '@components/performance/TournamentRanking';
import SubmittedRankingView from '@components/performance/SubmittedRankingView';
import EvaluatorTabStrip from '@components/performance/ranking/EvaluatorTabStrip';
import LegacyRankingEvaluationSidebarEvalItems from '@components/performance/ranking/LegacyRankingEvaluationSidebarEvalItems';
import { USE_LEGACY_RANKING_EVAL_SIDEBAR } from '../config/rankingEvaluationConfig';
import { CORE_SPORTS, getDefaultRankingSubcategory, getRankingSubcategoriesForSport } from '@utils/sports';
import { getPortalPaths, type PortalMode } from '@utils/portalPaths';
import { loadPlayerProfileForUser, buildFallbackPlayerProfile } from '@utils/coachPlayerProfile';
import {
  getRankingEvalLabel,
  getRankingEvaluationPathSegments,
  isRankingEvalTypeId,
  isRankingContentSubcategory,
  parseRankingEvaluationRoute,
  type RankingEvalTypeId,
} from '@utils/rankingEvaluationNav';

const DRAWER_WIDTH = 320;
const DRAWER_WIDTH_COLLAPSED = 64;

interface PlayerRankingEvaluationPageProps {
  portalMode?: PortalMode;
}

const PlayerRankingEvaluationPage: React.FC<PlayerRankingEvaluationPageProps> = ({ portalMode = 'player' }) => {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const paths = getPortalPaths(portalMode);
  const [error, setError] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Available sports - only Badminton is currently available
  const availableSports = CORE_SPORTS;
  const comingSoonSports = ['Cricket', 'Tennis', 'Athletics']; // Sports that are coming soon
  const skillLevels = [
    { value: 'beginner', label: 'Level 1: Beginner' },
    { value: 'intermediate', label: 'Level 2: Intermediate' },
    { value: 'advanced', label: 'Level 3: Advanced' },
    { value: 'pro', label: 'Level 4: Pro/Expert' }
  ];
  const validLevels = skillLevels.map((level) => level.value);
  const normalizeLevel = (rawLevel: string | null | undefined) => {
    const normalized = String(rawLevel || '').toLowerCase();
    return validLevels.includes(normalized) ? normalized : 'beginner';
  };
  const getContentTypesForSport = (sport: string) => getRankingSubcategoriesForSport(sport);
  const rankingEvalRef = useRef<RankingEvaluationWithTabsHandle>(null);
  const [embeddedState, setEmbeddedState] = useState<RankingEvaluationEmbeddedState>({
    metrics: { totalScore: 0, maxScore: 0, ratedCount: 0, notRatedCount: 0 },
    canRate: false,
    canDownloadManual: true,
    canDownloadReport: false,
    isSaving: false,
    ratingsDirty: false,
    isLoading: true,
  });

  // Get current path first (before using it)
  const currentPath = location.pathname;

  // IMPORTANT: Declare player state BEFORE using it
  const [player, setPlayer] = useState<any>(null);

  // Get current selection from URL (player will be null initially, so use 'Badminton' as default)
  const urlParams = new URLSearchParams(location.search);
  const currentSport = urlParams.get('sport') || 'Badminton';
  const currentLevel = normalizeLevel(urlParams.get('level'));
  const pathSegments = getRankingEvaluationPathSegments(currentPath);
  const pathSegment = pathSegments[pathSegments.length - 1] || 'strokes-shots';
  const parsedRoute = parseRankingEvaluationRoute(currentPath, urlParams, currentSport);
  const currentSubcategory = parsedRoute.contentSubcategory;
  const currentEvalType = parsedRoute.evalType;
  const currentCategory = USE_LEGACY_RANKING_EVAL_SIDEBAR
    ? (isRankingEvalTypeId(pathSegment) ? pathSegment : parsedRoute.isSpecialRoute ? parsedRoute.pathSegment : pathSegment)
    : parsedRoute.isSpecialRoute
      ? parsedRoute.pathSegment
      : parsedRoute.contentSubcategory;
  const isEvaluationContentView = parsedRoute.isEvaluationContent && !parsedRoute.isSpecialRoute;

  // Auto-expand current path (cascading expansion) - initialize from URL params
  const getInitialExpandedItems = () => {
    const params = new URLSearchParams(location.search);
    const sport = params.get('sport') || 'Badminton';
    const level = normalizeLevel(params.get('level'));
    const requestedSport = params.get('sport') || 'Badminton';
    const defaultSubcategory = getDefaultRankingSubcategory(requestedSport);
    const subcategory = params.get('subcategory') || defaultSubcategory;
    return new Set<string>([
      sport,
      `${sport}-${level}`,
      `${sport}-${level}-${subcategory}`,
    ]);
  };

  const [expandedItems, setExpandedItems] = useState<Set<string>>(getInitialExpandedItems());

  // Update expanded items when URL changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sport = params.get('sport') || (player?.primarySport || 'Badminton');
    const level = normalizeLevel(params.get('level'));
    const subcategory = params.get('subcategory') || getDefaultRankingSubcategory(sport);
    const newExpanded = new Set<string>([
      sport,
      `${sport}-${level}`,
      `${sport}-${level}-${subcategory}`,
    ]);
    setExpandedItems(newExpanded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.pathname, player?.primarySport]);

  const toggleExpanded = (key: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const getSportIcon = (sport: string) => {
    switch (sport) {
      case 'Badminton': return <BadmintonIcon />;
      case 'Cricket': return <CricketIcon />;
      case 'Tennis': return <TennisIcon />;
      case 'Football': return <FootballIcon />;
      case 'Ice Hockey': return <IceHockeyIcon />;
      case 'Athletics': return <AthleticsIcon />;
      default: return <RankingIcon />;
    }
  };

  // IMPORTANT:
  // Ranking APIs expect the *players.id* UUID, not the users.id UUID.
  // The dashboard correctly resolves this via /api/players/me?userId=...,
  // so we do the same here to ensure "View" loads rated values.
  // (player state is declared above at line 66)

  const loadPlayerDetails = async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      const profile = await loadPlayerProfileForUser(
        user.id,
        token,
        portalMode === 'coach',
        user
      );
      if (profile) {
        setPlayer(profile);
      } else {
        setError('Failed to load player details');
      }
    } catch (e: any) {
      console.error('Error loading player details:', e);
      if (portalMode === 'coach' && user) {
        setPlayer(buildFallbackPlayerProfile(user));
      } else {
        setError(e?.message || 'Error loading player details');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlayerDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Redirect to default route if query params are missing (handle old URLs)
  useEffect(() => {
    if (player?.id && !loading) {
      const urlParams = new URLSearchParams(location.search);
      const hasSport = urlParams.has('sport');
      const hasLevel = urlParams.has('level');

      if (!hasSport && !hasLevel) {
        const defaultSport = player.primarySport || 'Badminton';
        const defaultLevel = 'beginner';
        const defaultSubcategory = getDefaultRankingSubcategory(defaultSport);
        const segments = getRankingEvaluationPathSegments(location.pathname);
        const segment = segments[segments.length - 1] || defaultSubcategory;
        const targetSubcategory =
          segments.length >= 2 && isRankingContentSubcategory(segments[0])
            ? segments[0]
            : isRankingContentSubcategory(segment)
              ? segment
              : defaultSubcategory;
        const targetEval =
          segments.length >= 2 && isRankingEvalTypeId(segments[1])
            ? segments[1]
            : isRankingEvalTypeId(segment)
              ? segment
              : 'self-evaluation';

        navigate(
          paths.rankingEvaluation(targetSubcategory, {
            sport: defaultSport,
            level: defaultLevel,
            eval: targetEval,
          }),
          { replace: true }
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player?.id, loading, location.search, location.pathname]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleBack = () => {
    navigate(paths.rankingOverview);
  };

  // Legacy sidebar: evaluator nested under content type
  const handleLegacyEvalNavigation = (sport: string, level: string, subcategory: string, evaluationType: string) => {
    if (comingSoonSports.includes(sport)) return;
    navigate(paths.rankingEvaluation(evaluationType, { sport, level, subcategory }));
  };

  const handleContentNavigation = (sport: string, level: string, subcategory: string, evalType: RankingEvalTypeId = currentEvalType) => {
    if (comingSoonSports.includes(sport)) return;
    navigate(paths.rankingEvaluation(subcategory, { sport, level, eval: evalType }));
  };

  const handleEvalTabChange = (evalType: RankingEvalTypeId) => {
    handleContentNavigation(currentSport, currentLevel, currentSubcategory, evalType);
  };

  // Normalize legacy URLs to /:subcategory/:evalType tab routes
  useEffect(() => {
    if (USE_LEGACY_RANKING_EVAL_SIDEBAR) return;
    if (!player?.id || loading) return;
    if (parsedRoute.isSpecialRoute) return;

    const sport = urlParams.get('sport') || player.primarySport || 'Badminton';
    const level = normalizeLevel(urlParams.get('level'));
    const subcategory = parsedRoute.contentSubcategory || getDefaultRankingSubcategory(sport);
    const evalType = parsedRoute.evalType;
    const needsTwoSegmentPath =
      pathSegments.length < 2 ||
      !isRankingContentSubcategory(pathSegments[0]) ||
      !isRankingEvalTypeId(pathSegments[1]);
    const legacyEvalInPath = pathSegments.length === 1 && isRankingEvalTypeId(pathSegment);
    const legacyEvalInQuery = pathSegments.length === 1 && isRankingContentSubcategory(pathSegment) && urlParams.has('eval');

    if (needsTwoSegmentPath || legacyEvalInPath || legacyEvalInQuery) {
      navigate(paths.rankingEvaluation(subcategory, { sport, level, eval: evalType }), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player?.id, loading, location.pathname, location.search]);

  useEffect(() => {
    setEmbeddedState({
      metrics: { totalScore: 0, maxScore: 0, ratedCount: 0, notRatedCount: 0 },
      canRate: false,
      canDownloadManual: true,
      canDownloadReport: false,
      isSaving: false,
      ratingsDirty: false,
      isLoading: true,
    });
  }, [currentEvalType, currentSubcategory]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (!user) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">Please log in to view ranking evaluation data.</Alert>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (loading || !player?.id) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  const drawer = (
    <Box sx={{ bgcolor: '#2c3e50', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ 
        display: 'flex', 
        justifyContent: sidebarCollapsed ? 'center' : 'space-between', 
        alignItems: 'center', 
        minHeight: '48px !important', 
        bgcolor: '#A05AFF', 
        gap: 1,
        flexDirection: sidebarCollapsed ? 'column' : 'row',
        py: sidebarCollapsed ? 1 : 0,
      }}>
        {!sidebarCollapsed && (
          <EncycdataLogo height={28} lightBackground sx={{ mr: 1 }} />
        )}
        {!sidebarCollapsed && (
          <IconButton
            onClick={handleBack}
            sx={{
              color: '#ff9800',
              bgcolor: 'rgba(255, 152, 0, 0.2)',
              '&:hover': {
                bgcolor: 'rgba(255, 152, 0, 0.3)',
              },
              width: 36,
              height: 36,
              border: '1px solid rgba(255, 152, 0, 0.5)',
            }}
            size="small"
          >
            <ArrowBackIcon />
          </IconButton>
        )}
        {!sidebarCollapsed && (
          <Typography variant="h6" noWrap component="div" sx={{ color: '#ffffff', fontWeight: 700, flexGrow: 1 }}>
            {portalMode === 'coach' ? 'Learn and Rank' : 'Ranking Evaluation'}
          </Typography>
        )}
        <IconButton
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          sx={{ 
            color: '#ffffff',
            ml: sidebarCollapsed ? 0 : 'auto',
            bgcolor: sidebarCollapsed ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.2)',
            },
            width: sidebarCollapsed ? 40 : 'auto',
            height: sidebarCollapsed ? 40 : 'auto',
          }}
          size="small"
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {sidebarCollapsed ? <ChevronRightIcon sx={{ fontSize: 28 }} /> : <ChevronLeftIcon />}
        </IconButton>
      </Toolbar>
      <List>

        {availableSports.map((sport) => {
          const isSportExpanded = expandedItems.has(sport);
          const isSportActive = currentSport === sport;
          const isComingSoon = comingSoonSports.includes(sport);
          
          return (
            <React.Fragment key={sport}>
              {/* Sport Level */}
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => !isComingSoon && toggleExpanded(sport)}
                  selected={isSportActive && !isComingSoon}
                  disabled={isComingSoon}
                  sx={{
                    color: isComingSoon ? '#95a5a6' : (isSportActive ? '#ffffff' : '#ecf0f1'),
                    bgcolor: isComingSoon ? '#34495e' : (isSportActive ? '#34495e' : 'transparent'),
                    opacity: isComingSoon ? 0.6 : 1,
                    fontWeight: isSportActive ? 700 : 600,
                    borderLeft: isSportActive && !isComingSoon ? '8px solid #ff9800' : '8px solid transparent',
                    '&:hover': {
                      bgcolor: isComingSoon ? '#34495e' : (isSportActive ? '#3d566e' : '#34495e'),
                      borderLeft: isComingSoon ? '8px solid transparent' : (isSportActive ? '8px solid #ff9800' : '8px solid #7f8c8d'),
                    },
                    '&.Mui-selected': {
                      bgcolor: '#34495e',
                      color: '#ffffff',
                      borderLeft: '8px solid #ff9800',
                      '&:hover': {
                        bgcolor: '#3d566e',
                        borderLeft: '8px solid #ff9800',
                      },
                    },
                    '&.Mui-disabled': {
                      opacity: 0.6,
                      bgcolor: '#34495e',
                      color: '#95a5a6',
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: isComingSoon ? '#95a5a6' : '#ff9800', minWidth: sidebarCollapsed ? 0 : 56 }}>
                    {getSportIcon(sport)}
                  </ListItemIcon>
                  {!sidebarCollapsed && (
                    <>
                      <ListItemText
                        primary={sport + (isComingSoon ? ' (Coming Soon)' : '')}
                        primaryTypographyProps={{ 
                          fontSize: '0.9rem', 
                          fontWeight: isSportActive && !isComingSoon ? 700 : 600,
                          color: isComingSoon ? '#95a5a6' : (isSportActive ? '#ff9800' : '#ecf0f1'),
                        }}
                      />
                      {!isComingSoon && (isSportExpanded ? <ExpandLessIcon sx={{ color: isSportActive ? '#ff9800' : '#ecf0f1' }} /> : <ExpandMoreIcon sx={{ color: isSportActive ? '#ff9800' : '#ecf0f1' }} />)}
                    </>
                  )}
                </ListItemButton>
              </ListItem>

              {/* Skill Levels - Only show if sport is not coming soon */}
              {!isComingSoon && (
              <Collapse in={isSportExpanded} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {/* Submitted Ranking - list and compare reports for this sport */}
                  <ListItemButton
                    sx={{
                      pl: 4,
                      bgcolor: currentCategory === 'submitted-ranking' && currentSport === sport ? '#2e7d32' : 'transparent',
                      color: currentCategory === 'submitted-ranking' && currentSport === sport ? '#ffffff' : '#bdc3c7',
                      '&:hover': { bgcolor: currentCategory === 'submitted-ranking' && currentSport === sport ? '#1b5e20' : '#34495e' },
                    }}
                    selected={currentCategory === 'submitted-ranking' && currentSport === sport}
                    onClick={() => navigate(paths.rankingEvaluation('submitted-ranking', { sport }))}
                  >
                    {!sidebarCollapsed && (
                      <>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <ListIcon sx={{ fontSize: 18, color: currentCategory === 'submitted-ranking' && currentSport === sport ? '#ffffff' : '#95a5a6' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary="Submitted Ranking"
                          primaryTypographyProps={{
                            fontSize: '0.9rem',
                            fontWeight: currentCategory === 'submitted-ranking' && currentSport === sport ? 700 : 600,
                            color: currentCategory === 'submitted-ranking' && currentSport === sport ? '#ffffff' : '#bdc3c7',
                          }}
                        />
                      </>
                    )}
                  </ListItemButton>
                  {skillLevels.map((level) => {
                    const levelKey = `${sport}-${level.value}`;
                    const isLevelExpanded = expandedItems.has(levelKey);
                    const isLevelActive = currentSport === sport && currentLevel === level.value;
                    
                    return (
                      <React.Fragment key={level.value}>
                        <ListItemButton
                          sx={{ 
                            pl: 4,
                            bgcolor: 'transparent',
                            color: '#bdc3c7',
                            '&:hover': {
                              bgcolor: '#34495e',
                            },
                            '&.Mui-selected': {
                              bgcolor: 'transparent',
                              color: '#bdc3c7',
                              '&:hover': {
                                bgcolor: '#34495e',
                              },
                            },
                          }}
                          selected={isLevelActive}
                          onClick={() => toggleExpanded(levelKey)}
                        >
                          {!sidebarCollapsed && (
                            <>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                {isLevelActive ? (
                                  <StarIcon sx={{ 
                                    fontSize: 18, 
                                    color: '#9c27b0',
                                    filter: 'drop-shadow(0 0 4px rgba(255, 152, 0, 0.8))',
                                    stroke: '#ff9800',
                                    strokeWidth: 2,
                                    strokeLinejoin: 'round',
                                  }} />
                                ) : (
                                  <StarIcon sx={{ fontSize: 18, opacity: 0.5, color: '#95a5a6' }} />
                                )}
                              </ListItemIcon>
                              <ListItemText
                                primary={level.label}
                                primaryTypographyProps={{ 
                                  fontSize: '0.9rem',
                                  fontWeight: isLevelActive ? 700 : 600,
                                  color: '#bdc3c7'
                                }}
                              />
                              {isLevelExpanded ? <ExpandLessIcon sx={{ fontSize: 16, color: '#95a5a6' }} /> : <ExpandMoreIcon sx={{ fontSize: 16, color: '#95a5a6' }} />}
                            </>
                          )}
                        </ListItemButton>

                        {/* Content Types */}
                        <Collapse in={isLevelExpanded} timeout="auto" unmountOnExit>
                          <List component="div" disablePadding>
                            {getContentTypesForSport(sport).map((contentType) => {
                              const contentTypeKey = `${sport}-${level.value}-${contentType}`;
                              const isContentTypeExpanded = expandedItems.has(contentTypeKey);
                              const isContentTypeActive =
                                currentSport === sport &&
                                currentLevel === level.value &&
                                currentSubcategory === contentType &&
                                (USE_LEGACY_RANKING_EVAL_SIDEBAR
                                  ? isRankingEvalTypeId(currentCategory)
                                  : isEvaluationContentView);
                              const contentTypeLabels: { [key: string]: string } = {
                                'strokes-shots': getDefaultRankingSubcategory(sport) === 'shooting' ? 'Shooting' : 'Strokes & Shots',
                                'shooting': 'Shooting',
                                'drills': 'Drills',
                                'fitness': 'Fitness',
                              };
                              
                              return (
                                <React.Fragment key={contentType}>
                                  <ListItemButton
                                    sx={{ 
                                      pl: 6,
                                      bgcolor: isContentTypeActive ? '#6a1b9a' : 'transparent',
                                      color: isContentTypeActive ? '#ffffff' : '#bdc3c7',
                                      '&:hover': {
                                        bgcolor: isContentTypeActive ? '#4a148c' : '#34495e',
                                      },
                                      '&.Mui-selected': {
                                        bgcolor: '#6a1b9a',
                                        color: '#ffffff',
                                        '&:hover': {
                                          bgcolor: '#4a148c',
                                        },
                                      },
                                    }}
                                    selected={isContentTypeActive}
                                    onClick={() => {
                                      if (USE_LEGACY_RANKING_EVAL_SIDEBAR) {
                                        toggleExpanded(contentTypeKey);
                                      } else {
                                        handleContentNavigation(sport, level.value, contentType);
                                      }
                                    }}
                                  >
                                    {!sidebarCollapsed && (
                                      <>
                                        <ListItemIcon sx={{ minWidth: 32 }}>
                                          {(contentType === 'strokes-shots' || contentType === 'shooting') ? (
                                            <Box
                                              component="svg"
                                              sx={{
                                                width: isContentTypeActive ? 16 : 14,
                                                height: isContentTypeActive ? 16 : 14,
                                                color: isContentTypeActive ? '#9c27b0' : '#95a5a6',
                                                filter: isContentTypeActive ? 'drop-shadow(0 0 6px rgba(255, 152, 0, 1)) drop-shadow(0 0 3px rgba(255, 152, 0, 0.8))' : 'none',
                                                stroke: isContentTypeActive ? '#ff9800' : 'transparent',
                                                strokeWidth: isContentTypeActive ? 2 : 0,
                                              }}
                                              viewBox="0 0 24 24"
                                              fill="currentColor"
                                            >
                                              {/* Shuttlecock shape: circle with feathers */}
                                              <circle cx="12" cy="8" r="3" fill="currentColor" />
                                              <path d="M12 11 L10 20 L12 22 L14 20 Z" fill="currentColor" />
                                              <path d="M9 12 L7 18 L9 20 L11 18 Z" fill="currentColor" />
                                              <path d="M15 12 L13 18 L15 20 L17 18 Z" fill="currentColor" />
                                            </Box>
                                          ) : (
                                            <DiamondIcon sx={{ 
                                              fontSize: isContentTypeActive ? 16 : 14, 
                                              transform: 'rotate(45deg)',
                                              color: isContentTypeActive ? '#9c27b0' : '#95a5a6',
                                              filter: isContentTypeActive ? 'drop-shadow(0 0 6px rgba(255, 152, 0, 1)) drop-shadow(0 0 3px rgba(255, 152, 0, 0.8))' : 'none',
                                              stroke: isContentTypeActive ? '#ff9800' : 'transparent',
                                              strokeWidth: isContentTypeActive ? 2 : 0,
                                              strokeLinejoin: 'round',
                                            }} />
                                          )}
                                        </ListItemIcon>
                                        <ListItemText
                                          primary={contentTypeLabels[contentType]}
                                          primaryTypographyProps={{ 
                                            fontSize: '0.9rem',
                                            fontWeight: isContentTypeActive ? 700 : 600,
                                            color: '#bdc3c7'
                                          }}
                                        />
                                        {USE_LEGACY_RANKING_EVAL_SIDEBAR &&
                                          (isContentTypeExpanded ? (
                                            <ExpandLessIcon sx={{ fontSize: 16, color: '#95a5a6' }} />
                                          ) : (
                                            <ExpandMoreIcon sx={{ fontSize: 16, color: '#95a5a6' }} />
                                          ))}
                                      </>
                                    )}
                                  </ListItemButton>

                                  {USE_LEGACY_RANKING_EVAL_SIDEBAR && (
                                    <LegacyRankingEvaluationSidebarEvalItems
                                      sport={sport}
                                      level={level.value}
                                      contentType={contentType}
                                      sidebarCollapsed={sidebarCollapsed}
                                      currentSport={currentSport}
                                      currentLevel={currentLevel}
                                      currentSubcategory={currentSubcategory}
                                      currentCategory={currentCategory}
                                      isContentTypeExpanded={isContentTypeExpanded}
                                      onNavigate={handleLegacyEvalNavigation}
                                    />
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </List>
                        </Collapse>
                      </React.Fragment>
                    );
                  })}
                </List>
              </Collapse>
              )}
            </React.Fragment>
          );
        })}
      </List>
      <Box sx={{ mt: 'auto', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <ListItem disablePadding>
            <ListItemButton 
            onClick={handleLogout}
            sx={{
              color: '#ff9800',
              '&:hover': {
                bgcolor: 'rgba(255, 152, 0, 0.1)',
              },
            }}
          >
            <ListItemIcon sx={{ color: '#ff9800', minWidth: sidebarCollapsed ? 0 : 56 }}>
              <LogoutIcon />
            </ListItemIcon>
            {!sidebarCollapsed && (
              <ListItemText 
                primary="Logout"
                primaryTypographyProps={{ 
                  fontSize: '0.9rem', 
                  fontWeight: 600,
                  color: '#ff9800'
                }}
              />
            )}
          </ListItemButton>
        </ListItem>
      </Box>
    </Box>
  );

  const renderContent = () => {
    // Show loading if player data is not loaded yet
    if (loading || !player || !player.id) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      );
    }

    // Tournament has different component
    if (currentCategory === 'tournament') {
      return <TournamentRanking player={player} />;
    }

    // Submitted Ranking – list and compare ranking reports per sport
    if (currentCategory === 'submitted-ranking') {
      const sportParam = new URLSearchParams(location.search).get('sport') || player?.primarySport || 'Badminton';
      if (comingSoonSports.includes(sportParam)) {
        return (
          <Box sx={{ p: 4 }}>
            <Alert severity="info">{sportParam} – Coming soon. Use Badminton for now.</Alert>
          </Box>
        );
      }
      return <SubmittedRankingView player={player} sport={sportParam} />;
    }

    // Get subcategory, level, and sport from URL params (with defaults)
    const urlParams = new URLSearchParams(location.search);
    const sport = urlParams.get('sport') || (player?.primarySport || 'Badminton');
    const defaultSubcategory = getDefaultRankingSubcategory(sport);
    const subcategory = urlParams.get('subcategory') || defaultSubcategory;
    const level = normalizeLevel(urlParams.get('level'));

    // Check if sport is coming soon
    if (comingSoonSports.includes(sport)) {
      return (
        <Box sx={{ p: 4 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              {sport} Ranking Evaluation - Coming Soon
            </Typography>
            <Typography variant="body2">
              We're currently developing ranking evaluation features for {sport}. 
              Please check back soon or use Badminton ranking evaluation in the meantime.
            </Typography>
          </Alert>
          <Button
            variant="contained"
            onClick={() =>
              navigate(
                paths.rankingEvaluation(currentCategory, {
                  sport: 'Badminton',
                  level,
                  subcategory,
                })
              )
            }
            sx={{ mt: 2 }}
          >
            Go to Badminton Ranking
          </Button>
        </Box>
      );
    }

    // Show loading if redirecting (query params missing)
    const hasAllParams = urlParams.has('sport') && urlParams.has('level');
    if (!hasAllParams && player?.id) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (!player || !player.id) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <Alert severity="error">Player information is missing. Please refresh the page.</Alert>
        </Box>
      );
    }

    const evalTypeForContent = USE_LEGACY_RANKING_EVAL_SIDEBAR
      ? (isRankingEvalTypeId(currentCategory) ? currentCategory : 'self-evaluation')
      : currentEvalType;
    const levelLabel = skillLevels.find((item) => item.value === level)?.label || level;

    if (!USE_LEGACY_RANKING_EVAL_SIDEBAR) {
      return (
        <EvaluatorTabStrip
          sport={sport}
          levelLabel={levelLabel}
          subcategory={subcategory}
          activeEval={evalTypeForContent}
          onEvalChange={handleEvalTabChange}
          onDownloadManual={() => rankingEvalRef.current?.triggerDownloadManual()}
          onDownloadReport={() => rankingEvalRef.current?.triggerDownloadReport()}
          isSaving={embeddedState.isSaving}
          ratingsDirty={embeddedState.ratingsDirty}
          canRate={embeddedState.canRate}
          canDownloadManual={embeddedState.canDownloadManual}
          canDownloadReport={embeddedState.canDownloadReport}
          metrics={embeddedState.metrics}
        >
          <RankingEvaluationWithTabs
            key={evalTypeForContent}
            ref={rankingEvalRef}
            player={player}
            categoryId={evalTypeForContent}
            categoryLabel={getRankingEvalLabel(evalTypeForContent, 'tab')}
            coachId={undefined}
            subcategory={subcategory}
            skillLevel={level}
            defaultSport={sport}
            embeddedInEvaluatorTabs
            hideHeaderSaveDownload
            onEmbeddedStateChange={setEmbeddedState}
          />
        </EvaluatorTabStrip>
      );
    }

    return (
      <RankingEvaluationWithTabs
        ref={rankingEvalRef}
        player={player}
        categoryId={evalTypeForContent}
        categoryLabel={getRankingEvalLabel(evalTypeForContent, 'legacy')}
        coachId={undefined}
        subcategory={subcategory}
        skillLevel={level}
        defaultSport={sport}
      />
    );
  };

  if (currentCategory === 'contest') {
    return <Navigate to={paths.rankingOverview} replace />;
  }

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Sidebar */}
      <Box
        component="nav"
        sx={{ width: { sm: sidebarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH }, flexShrink: { sm: 0 }, transition: 'width 0.3s' }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: sidebarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
              bgcolor: '#e8f4f8',
              transition: 'width 0.3s'
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: sidebarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
              bgcolor: '#e8f4f8',
              transition: 'width 0.3s',
              overflowX: 'hidden'
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${sidebarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH}px)` },
          mt: 0, // Removed top margin since AppBar is removed
          transition: 'width 0.3s, margin 0.3s'
        }}
      >
        <Container maxWidth={false} sx={{ maxWidth: { xs: '100%', sm: 'calc(100vw - 360px)' }, px: { xs: 2, sm: 3 } }}>
          {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}
          {renderContent()}
        </Container>
      </Box>
    </Box>
  );
};

export default PlayerRankingEvaluationPage;

